import { motion } from 'framer-motion';

const certifications = [
  {
    name: "ISO 27001",
    description: "Sécurité de l'information",
    icon: "ri-shield-check-line",
    color: "#0EA5E9"
  },
  {
    name: "SOC 2 Type II",
    description: "Conformité des données",
    icon: "ri-file-shield-2-line",
    color: "#8B5CF6"
  },
  {
    name: "GDPR Compliant",
    description: "Protection des données",
    icon: "ri-lock-line",
    color: "#10B981"
  },
  {
    name: "AI Ethics",
    description: "IA responsable",
    icon: "ri-brain-line",
    color: "#F59E0B"
  }
];

const awards = [
  {
    title: "Meilleure Innovation IA 2024",
    organization: "Tech Awards France",
    year: "2024",
    image: "https://public.readdy.ai/ai/img_res/e34a9044f622f082d3020233de58e8a8.jpg"
  },
  {
    title: "Top 10 Startups IA",
    organization: "French Tech",
    year: "2024",
    image: "https://readdy.ai/api/search-image?query=silver%20star%20award%20badge%20modern%20minimalist%20design%20elegant%20professional%20achievement%20recognition%20excellence%20certificate%20simple%20clean%20background&width=120&height=120&seq=award-star-2&orientation=squarish"
  },
  {
    title: "Excellence en Automatisation",
    organization: "Digital Innovation Summit",
    year: "2023",
    image: "https://readdy.ai/api/search-image?query=bronze%20medal%20award%20badge%20modern%20minimalist%20design%20elegant%20professional%20achievement%20recognition%20excellence%20certificate%20simple%20clean%20background&width=120&height=120&seq=award-medal-3&orientation=squarish"
  },
  {
    title: "Leader IA Conversationnelle",
    organization: "AI Europe Awards",
    year: "2023",
    image: "https://readdy.ai/api/search-image?query=platinum%20award%20ribbon%20badge%20modern%20minimalist%20design%20elegant%20professional%20achievement%20recognition%20excellence%20certificate%20simple%20clean%20background&width=120&height=120&seq=award-ribbon-4&orientation=squarish"
  }
];

const partners = [
  {
    name: "Google Cloud Partner",
    logo: "https://readdy.ai/api/search-image?query=google%20cloud%20partner%20logo%20official%20certification%20badge%20modern%20professional%20technology%20partnership%20emblem%20simple%20clean%20design&width=160&height=80&seq=partner-google&orientation=landscape"
  },
  {
    name: "Microsoft Azure",
    logo: "https://readdy.ai/api/search-image?query=microsoft%20azure%20partner%20logo%20official%20certification%20badge%20modern%20professional%20technology%20partnership%20emblem%20simple%20clean%20design&width=160&height=80&seq=partner-microsoft&orientation=landscape"
  },
  {
    name: "AWS Partner",
    logo: "https://readdy.ai/api/search-image?query=amazon%20aws%20partner%20logo%20official%20certification%20badge%20modern%20professional%20technology%20partnership%20emblem%20simple%20clean%20design&width=160&height=80&seq=partner-aws&orientation=landscape"
  },
  {
    name: "OpenAI Partner",
    logo: "https://readdy.ai/api/search-image?query=openai%20partner%20logo%20official%20certification%20badge%20modern%20professional%20artificial%20intelligence%20partnership%20emblem%20simple%20clean%20design&width=160&height=80&seq=partner-openai&orientation=landscape"
  }
];

export default function Certifications() {
  return (
    <section className="py-24 bg-gradient-to-b from-gray-950 to-black relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-cyber-blue/5 via-transparent to-[#39FF14]/5"></div>
      
      <div className="relative max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-4">
            Certifications &amp; Récompenses
          </h2>
          <p className="text-lg text-gray-400 font-body max-w-2xl mx-auto">
            Reconnus pour notre excellence et notre engagement envers la sécurité et l'innovation
          </p>
        </motion.div>

        {/* Certifications */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-2xl font-display font-bold text-white mb-8 text-center">
            Certifications de Sécurité
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all text-center"
              >
                <div 
                  className="w-16 h-16 flex items-center justify-center rounded-2xl mx-auto mb-4 transition-transform group-hover:scale-110"
                  style={{ 
                    backgroundColor: `${cert.color}20`,
                    boxShadow: `0 0 30px ${cert.color}30`
                  }}
                >
                  <i className={`${cert.icon} text-3xl`} style={{ color: cert.color }}></i>
                </div>
                <h4 className="text-lg font-display font-bold text-white mb-2">
                  {cert.name}
                </h4>
                <p className="text-sm text-gray-400 font-body">
                  {cert.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Awards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-2xl font-display font-bold text-white mb-8 text-center">
            Prix &amp; Distinctions
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {awards.map((award, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-cyber-blue/50 transition-all"
              >
                <div className="w-24 h-24 mx-auto mb-4 rounded-xl overflow-hidden">
                  <img
                    src={award.image}
                    alt={award.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="text-center">
                  <div className="inline-block px-3 py-1 bg-cyber-blue/20 rounded-full mb-3">
                    <span className="text-xs font-display font-bold text-cyber-blue">
                      {award.year}
                    </span>
                  </div>
                  <h4 className="text-base font-display font-bold text-white mb-2 leading-tight">
                    {award.title}
                  </h4>
                  <p className="text-sm text-gray-400 font-body">
                    {award.organization}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Partners */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-display font-bold text-white mb-8 text-center">
            Partenaires Technologiques
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {partners.map((partner, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group flex items-center justify-center p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all"
              >
                <div className="w-full h-16 flex items-center justify-center">
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="w-full h-full object-contain grayscale group-hover:grayscale-0 opacity-60 group-hover:opacity-100 transition-all duration-500"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Trust Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="inline-flex flex-col items-center gap-4 px-8 py-6 bg-gradient-to-r from-cyber-blue/20 to-[#39FF14]/20 backdrop-blur-sm rounded-2xl border border-white/10">
            <div className="flex items-center gap-2">
              <i className="ri-verified-badge-line text-3xl text-cyber-blue"></i>
              <i className="ri-award-line text-3xl text-[#39FF14]"></i>
              <i className="ri-medal-line text-3xl text-cyber-purple"></i>
            </div>
            <p className="text-white font-body text-sm max-w-2xl">
              <strong className="font-display">Certifié et reconnu</strong> par les plus grandes organisations internationales pour notre engagement envers la sécurité, la conformité et l'innovation en intelligence artificielle
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
